import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import PurchaseRequestApproveForm from "./PurchaseRequestApproveForm";
import {
  ArrowLeft,
  Calendar,
  User,
  Phone,
  MapPin,
  Package,
  DollarSign,
  Clock,
  FileText,
  Building,
  Truck,
  Info,
  CreditCard,
  Home,
} from "lucide-react";
import { useGetList } from "../../../../hooks/useGetList";
import formatDate from "../../../../Utils/formatDate";

const PurchaseRequestDetailList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useGetList(`v2/purchase-request/${id}`);

  const purchaseRequest = data?.data;

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading purchase request: {error.message}
      </Alert>
    );
  }

  if (!purchaseRequest) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Purchase request not found
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Chip
          label={purchaseRequest.status}
          color={getStatusColor(purchaseRequest.status)}
          size="medium"
        />
      </Box>

      {/* Request Information */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Info size={28} />
          </Box>
          <Typography variant="h4" fontWeight="bold">
            Request Details
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={3} mb={3}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FileText size={20} />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8, fontSize: "0.875rem" }}
                >
                  Request Code
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {purchaseRequest.code}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={3} mb={3}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Calendar size={20} />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8, fontSize: "0.875rem" }}
                >
                  Request Date
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatDate(purchaseRequest.purchaseRequestDate)}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={3}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Clock size={20} />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8, fontSize: "0.875rem" }}
                >
                  Required Date
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatDate(purchaseRequest.requiredDate)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            {purchaseRequest.reason && (
              <Box mb={3}>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8, fontSize: "0.875rem", mb: 1 }}
                >
                  Reason
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1.1rem", lineHeight: 1.6 }}
                >
                  {purchaseRequest.reason}
                </Typography>
              </Box>
            )}
            {purchaseRequest.remark && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8, fontSize: "0.875rem", mb: 1 }}
                >
                  Remark
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1.1rem", lineHeight: 1.6 }}
                >
                  {purchaseRequest.remark}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={4}>
        {/* Supplier Information */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 4,
              height: "100%",
              borderRadius: 3,
              boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 35px rgba(0,0,0,0.12)",
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={4}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                }}
              >
                <Truck size={28} />
              </Box>
              <Typography variant="h5" fontWeight="bold" color="#1976d2">
                Supplier Information
              </Typography>
            </Box>

            {purchaseRequest.relatedSupplier ? (
              <Box>
                <Box display="flex" alignItems="center" gap={3} mb={3}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <User size={20} color="#666" />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: "0.875rem" }}
                    >
                      Supplier Name
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="#333">
                      {purchaseRequest.relatedSupplier.name}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={3} mb={3}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Phone size={20} color="#666" />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: "0.875rem" }}
                    >
                      Contact Number
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="#333">
                      {purchaseRequest.relatedSupplier.phone}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="flex-start" gap={3} mb={3}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: 0.5,
                    }}
                  >
                    <MapPin size={20} color="#666" />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: "0.875rem", mb: 1 }}
                    >
                      Address
                    </Typography>
                    <Typography
                      variant="body1"
                      color="#333"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {purchaseRequest.relatedSupplier.address}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={3}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CreditCard size={20} color="#666" />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: "0.875rem" }}
                    >
                      Credit Limit
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="#333">
                      {purchaseRequest.relatedSupplier.creditAmount.toLocaleString()}{" "}
                      MMK
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                p={3}
                sx={{
                  backgroundColor: "#fafafa",
                  borderRadius: 2,
                  border: "1px dashed #ddd",
                }}
              >
                <Truck size={24} color="#999" />
                <Typography variant="body2" color="#666">
                  No supplier information available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Branch Information */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 4,
              height: "100%",
              borderRadius: 3,
              boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 35px rgba(0,0,0,0.12)",
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={4}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: "#e8f5e8",
                  color: "#2e7d32",
                }}
              >
                <Building size={28} />
              </Box>
              <Typography variant="h5" fontWeight="bold" color="#2e7d32">
                Branch Information
              </Typography>
            </Box>

            {purchaseRequest.relatedBranch ? (
              <Box>
                <Box display="flex" alignItems="center" gap={3} mb={3}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Home size={20} color="#666" />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: "0.875rem" }}
                    >
                      Branch Name
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="#333">
                      {purchaseRequest.relatedBranch.name}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="flex-start" gap={3} mb={3}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: 0.5,
                    }}
                  >
                    <Phone size={20} color="#666" />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: "0.875rem" }}
                    >
                      Contact Numbers
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="#333">
                      {purchaseRequest.relatedBranch.phones?.join(", ") ||
                        "N/A"}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="flex-start" gap={3}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: 0.5,
                    }}
                  >
                    <MapPin size={20} color="#666" />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: "0.875rem", mb: 1 }}
                    >
                      Branch Address
                    </Typography>
                    <Typography
                      variant="body1"
                      color="#333"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {purchaseRequest.relatedBranch.address}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                p={3}
                sx={{
                  backgroundColor: "#fafafa",
                  borderRadius: 2,
                  border: "1px dashed #ddd",
                }}
              >
                <Building size={24} color="#999" />
                <Typography variant="body2" color="#666">
                  No branch information available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Products Table */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Requested Products
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell>Expired Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseRequest.selectedProducts?.map((product) => (
                <TableRow key={product._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {product.relatedProduct?.name || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {product.relatedProduct?.code || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {product.relatedProductItem?.medicineItemName || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {product.relatedProductItem?.code || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {product.relatedItemUnit?.name || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {product.qty}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {product.purchasePrice?.toLocaleString() || 0} MMK
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {product.subtotal?.toLocaleString() || 0} MMK
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(product.expiredDate)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        {/* Total */}
        <Box display="flex" justifyContent="flex-end">
          <Box textAlign="right">
            <Typography variant="h6" fontWeight="bold">
              Total Amount: {purchaseRequest.total?.toLocaleString() || 0} MMK
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Purchase Approve Form - Only show if status is Pending */}
      {purchaseRequest?.status === "Pending" && (
        <PurchaseRequestApproveForm purchaseRequest={purchaseRequest} />
      )}
    </Box>
  );
};

export default PurchaseRequestDetailList;
